import { array } from 'fp-ts';
import { useState } from 'react';
import { compose, isEmpty, map, reject } from 'lodash/fp';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ConvertedCurrencyValue, LocalizedDateFromUtc, LocalizedDateTimeFromUtc, SpacedDash } from '@mews-commander/ui';
import { ExpandButton } from '@optimus-web/core';
import { isZeroAmount } from '@mews-commander/utils';
import { NoCommonBill, ProductOrder, ProductOrderGroup } from '../../../transformProductOrders/types';
import { BillLink, ProductOrderLabel } from '../../../../../components';
import { ItemsTableCellElement, ItemsTableClickableRowElement, TitleAndToggleElement } from '../styles';
import { UnassignedBillLink } from '../UnassignedBillLink';
import { CancelButton } from '../CancelButton';
import { ProductOrderItemRow } from '../ProductOrderItemRow';

interface ProductOrderGroupRowProps {
    group: ProductOrderGroup;
    depth?: number;
    isMultiTaxEnvironment: boolean;
    groupId: string;
    accountId: string;
    timeUnitKey: Mews.TextKey;
    cancelOrderItems: (orderItemIds: string[]) => void;
}

const renderGroupConsumption = (consumptionUtc: null | { first: string; last: string }) => {
    if (consumptionUtc) {
        const { first, last } = consumptionUtc;
        return first === last ? (
            <LocalizedDateTimeFromUtc utcString={first} />
        ) : (
            <>
                <LocalizedDateFromUtc utcString={first} />
                {SpacedDash}
                <LocalizedDateFromUtc utcString={last} />
            </>
        );
    }
    return UnicodeEntity.Dash;
};

const renderGroupBill = (bill: ProductOrderGroup['bill'], groupId: string, accountId: string) => {
    if (bill === NoCommonBill.Various) {
        return <LocalizedText textKey={TextKey.Various} />;
    }
    if (bill === NoCommonBill.None) {
        return <UnassignedBillLink groupId={groupId} accountId={accountId} />;
    }
    return <BillLink billId={bill} groupId={groupId} />;
};

const getNonCancelledOrderIds = compose(
    array.map(({ id }) => id),
    reject<ProductOrder>('isCanceled')
);

const renderGroupCancel = (orders: ProductOrderGroup['orders'], cancelOrderItems: (orderItemIds: string[]) => void) => {
    const nonCanceledOrderIds = getNonCancelledOrderIds(orders);
    const isCanceled = isEmpty(nonCanceledOrderIds);
    const cancel = (event: React.SyntheticEvent) => {
        cancelOrderItems(nonCanceledOrderIds);
        event.stopPropagation();
    };

    return <CancelButton isCancelled={isCanceled} handleCancel={cancel} />;
};

export const ProductOrderGroupRow = ({ group, depth = 0, ...other }: ProductOrderGroupRowProps) => {
    const [expanded, setExpanded] = useState(false);
    const isAllCancelled = isEmpty(getNonCancelledOrderIds(group.orders));

    const renderProductOrderSubgroups = (subgroup: ProductOrderGroup) => (
        <ProductOrderGroupRow group={subgroup} depth={depth + 1} {...other} />
    );
    const renderProductOrderItemRow = (order: ProductOrder) => (
        <ProductOrderItemRow
            order={order}
            key={order.id}
            cancelProductOrder={(orderItemId: string) => other.cancelOrderItems([orderItemId])}
            isMultiTaxEnvironment={other.isMultiTaxEnvironment}
            groupId={other.groupId}
            accountId={other.accountId}
            timeUnitKey={other.timeUnitKey}
        />
    );
    if (array.size(group.subgroups) === 1) {
        return <ProductOrderGroupRow group={group.subgroups[0]} depth={depth} {...other} />;
    }
    if (array.size(group.orders) === 1) {
        return (
            <ProductOrderItemRow
                order={group.orders[0]}
                cancelProductOrder={(orderItemId: string) => other.cancelOrderItems([orderItemId])}
                isMultiTaxEnvironment={other.isMultiTaxEnvironment}
                groupId={other.groupId}
                accountId={other.accountId}
                timeUnitKey={other.timeUnitKey}
                depth={depth}
                key={other.groupId}
                isSingleOrderGroup
            />
        );
    }
    return (
        <>
            <ItemsTableClickableRowElement onClick={() => setExpanded(!expanded)}>
                <ItemsTableCellElement offset={depth}>
                    <TitleAndToggleElement>
                        <ExpandButton expanded={expanded} />
                        <ProductOrderLabel
                            productId={group.productId}
                            totalNumberOfProducts={group.totalNumberOfProducts}
                            ageCategoryId={group.ageCategoryId}
                        />
                    </TitleAndToggleElement>
                </ItemsTableCellElement>
                <ItemsTableCellElement>
                    {isAllCancelled ? (
                        UnicodeEntity.Dash
                    ) : (
                        <ConvertedCurrencyValue amount={group.totalCost} net={other.isMultiTaxEnvironment} />
                    )}
                </ItemsTableCellElement>
                <ItemsTableCellElement>{renderGroupConsumption(group.consumptionUtc)}</ItemsTableCellElement>
                <ItemsTableCellElement>
                    {isAllCancelled || isZeroAmount(group.totalCost)
                        ? UnicodeEntity.Dash
                        : renderGroupBill(group.bill, other.groupId, other.accountId)}
                </ItemsTableCellElement>
                <ItemsTableCellElement>{renderGroupCancel(group.orders, other.cancelOrderItems)}</ItemsTableCellElement>
            </ItemsTableClickableRowElement>
            {expanded &&
                (array.isNonEmpty(group.subgroups)
                    ? map(renderProductOrderSubgroups, group.subgroups)
                    : map(renderProductOrderItemRow, group.orders))}
        </>
    );
};
